// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraDirection, CameraSource } from "@capacitor/camera";
import { isDefined } from "@clipboard-health/util-ts";
import { useTheme } from "@mui/material";
import { FullScreenDialog, isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { IGNORE_CAMERA_ERROR_MESSAGES } from "@src/appV2/lib/files/constants";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { WorkBadgeReviewPolicySource } from "../../../../WorkBadges/components/Policy/types";
import { WorkBadgeReviewPolicyStep } from "../../../../WorkBadges/hooks/useWorkBadgeReviewPolicy";
import { getWorkBadgeReviewPolicyUrl } from "../../../../WorkBadges/utils/getWorkBadgeReviewPolicyUrl";
import { SHIFT_DETAILS_BADGE_VERIFICATION_STEP_QUERY_PARAM } from "../../../path";
import {
  BadgeVerificationWindowDays,
  useGetWorkerBadgeVerifications,
} from "./api/useGetWorkerBadgeVerifications";
import { BadgeVerificationConfirmContentRedesign } from "./BadgeVerificationConfirmContentRedesign";
import { BadgeVerificationTakePhotoContent } from "./BadgeVerificationTakePhotoContent";
import { BADGE_VERIFICATION_IMAGE_WIDTH } from "./constants";
import { useBadgeVerificationSubmit } from "./hooks/useBadgeVerificationSubmit";
import { NoBadgeContentRedesign } from "./NoBadgeContentRedesign";
import { SkipBadgeVerificationContent } from "./SkipBadgeVerificationContent";
import { SkippedBadgeVerificationContent } from "./SkippedBadgeVerificationContent";
import { type BadgeVerificationPhoto, BadgeVerificationStep } from "./types";

const MAX_MISSED_BEFORE_RESTRICTION = 3;

interface BadgeVerificationDialogRedesignProps {
  shiftId: string;
  qualificationName: string;
  modalState: UseModalState;
  initialStep?: BadgeVerificationStep;
  onSubmitPhoto: () => void;
  onSkipBadgeVerification: () => void;
}

export function BadgeVerificationDialogRedesign(props: BadgeVerificationDialogRedesignProps) {
  const {
    shiftId,
    qualificationName,
    modalState,
    initialStep,
    onSubmitPhoto,
    onSkipBadgeVerification,
  } = props;

  const history = useHistory();
  const theme = useTheme();
  const { showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const { missedVerifications } = useGetWorkerBadgeVerifications(
    worker.userId,
    BadgeVerificationWindowDays.MISSED_BADGES,
    { includeMissedBadges: true, enabled: modalState.modalIsOpen }
  );
  const unverifiedBadgeCount = missedVerifications.length;

  const [step, setStep] = useState(initialStep ?? BadgeVerificationStep.INTRO);
  const [photo, setPhoto] = useState<BadgeVerificationPhoto | undefined>(undefined);

  useEffect(() => {
    if (isDefined(initialStep)) {
      setStep(initialStep);
    }
  }, [initialStep]);

  const { handleSubmit, handleSkipToClockIn, isSubmitting } = useBadgeVerificationSubmit({
    shiftId,
    photo,
    onSubmitPhoto,
    onSkipSuccess: () => {
      // After they skip it will be one higher than unverified badge count
      // unverifiedBadgeCount is captured here and won't update when the request is sent
      if (unverifiedBadgeCount + 1 >= MAX_MISSED_BEFORE_RESTRICTION) {
        onSkipBadgeVerification();
      } else {
        setStep(BadgeVerificationStep.SKIP_RESULT);
      }
    },
  });

  useLogEffect(
    APP_V2_USER_EVENTS.BADGE_VERIFICATION_DIALOG_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const resetState = useCallback(() => {
    setStep(BadgeVerificationStep.INTRO);
    setPhoto(undefined);
  }, []);

  // If we reach the CONFIRM step without a photo (shouldn't happen normally),
  // reset back to INTRO to avoid rendering an empty confirm screen.
  useEffect(() => {
    if (step === BadgeVerificationStep.CONFIRM && !isDefined(photo)) {
      setStep(BadgeVerificationStep.INTRO);
    }
  }, [step, photo]);

  const takePhoto = useTakePhoto({
    source: isCapacitorPlatform() ? CameraSource.Camera : CameraSource.Photos,
    onSuccess: (photoData: UploadFileData) => {
      setPhoto({ ...photoData, previewUrl: photoData.dataUrlBase64 });
      setStep(BadgeVerificationStep.CONFIRM);
    },
    onError: (error) => {
      if (error instanceof CameraPermissionDeniedError) {
        showErrorToast("Camera permission is required to take a badge photo.");
      } else if (IGNORE_CAMERA_ERROR_MESSAGES.has(error.message)) {
        // User cancelled camera - don't show error toast
      } else {
        showErrorToast("Failed to take photo. Please try again.");
      }
    },
    photoOptions: {
      direction: CameraDirection.Front,
      width: BADGE_VERIFICATION_IMAGE_WIDTH,
    },
  });

  const navigateToBadgePolicyStep = useCallback(
    (policyStep: WorkBadgeReviewPolicyStep) => {
      const policyUrl = getWorkBadgeReviewPolicyUrl({
        source: WorkBadgeReviewPolicySource.BADGE_VERIFICATION,
        shiftId,
        qualificationName,
        step: policyStep,
      });
      // Persist the current step so badge verification reopens at the right
      // screen when the user navigates back from the policy page
      const params = new URLSearchParams(history.location.search);
      params.set(SHIFT_DETAILS_BADGE_VERIFICATION_STEP_QUERY_PARAM, step);
      history.replace({
        pathname: history.location.pathname,
        search: params.toString(),
        state: history.location.state,
      });
      history.push(policyUrl);
    },
    [history, shiftId, qualificationName, step]
  );

  const navigateToBadgePolicy = useCallback(() => {
    navigateToBadgePolicyStep(WorkBadgeReviewPolicyStep.REVIEW_POLICY);
  }, [navigateToBadgePolicyStep]);

  const navigateToGetBadge = useCallback(() => {
    navigateToBadgePolicyStep(WorkBadgeReviewPolicyStep.SELECT_HEADSHOT);
  }, [navigateToBadgePolicyStep]);

  const handleClose = useCallback(() => {
    modalState.closeModal();
    // Reset state after modal exit transition completes
    setTimeout(resetState, theme.transitions.duration.leavingScreen);
  }, [modalState, resetState, theme.transitions.duration.leavingScreen]);

  function renderStep() {
    switch (step) {
      case BadgeVerificationStep.INTRO: {
        return (
          <BadgeVerificationTakePhotoContent
            onBack={handleClose}
            onTakePhoto={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_TAKE_PHOTO_TAPPED, { shiftId });
              void takePhoto();
            }}
            onNoBadge={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_NO_BADGE_TAPPED, { shiftId });
              setStep(BadgeVerificationStep.NO_BADGE);
            }}
            onWhyBadgeRequired={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_WHY_BADGE_REQUIRED_TAPPED, {
                shiftId,
              });
              navigateToBadgePolicy();
            }}
          />
        );
      }

      case BadgeVerificationStep.CONFIRM: {
        if (!isDefined(photo)) {
          // useEffect above will reset to INTRO on next render
          return undefined;
        }

        return (
          <BadgeVerificationConfirmContentRedesign
            photoPreviewUrl={photo.previewUrl}
            isSubmitting={isSubmitting}
            onBack={() => {
              setStep(BadgeVerificationStep.INTRO);
              setPhoto(undefined);
            }}
            onSubmit={handleSubmit}
            onRetakePhoto={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RETAKE_PHOTO_TAPPED, { shiftId });
              void takePhoto();
            }}
          />
        );
      }

      case BadgeVerificationStep.NO_BADGE: {
        return (
          <NoBadgeContentRedesign
            isSubmitting={isSubmitting}
            onBack={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_HAS_BADGE_TAPPED, { shiftId });
              setStep(BadgeVerificationStep.INTRO);
            }}
            onPickedUpBadge={() => setStep(BadgeVerificationStep.INTRO)}
            onSkipToClockIn={() => setStep(BadgeVerificationStep.SKIP_CONFIRM)}
          />
        );
      }

      case BadgeVerificationStep.SKIP_CONFIRM: {
        return (
          <SkipBadgeVerificationContent
            isSubmitting={isSubmitting}
            onBack={() => setStep(BadgeVerificationStep.NO_BADGE)}
            onConfirmSkip={handleSkipToClockIn}
            onGetTemporaryBadge={() => setStep(BadgeVerificationStep.NO_BADGE)}
          />
        );
      }

      case BadgeVerificationStep.SKIP_RESULT: {
        return (
          <SkippedBadgeVerificationContent
            unverifiedBadgeCount={unverifiedBadgeCount}
            onClockIn={onSkipBadgeVerification}
            onViewBadgePolicy={navigateToBadgePolicy}
            onNeedNewBadge={navigateToGetBadge}
          />
        );
      }

      default: {
        const exhaustiveCheck: never = step;
        return exhaustiveCheck;
      }
    }
  }

  return (
    <FullScreenDialog
      skipTopSafeAreaPadding
      modalState={modalState}
      variant="tertiary"
      // Disable transition so navigating back from policy page opens dialog instantly
      transitionDuration={0}
    >
      {renderStep()}
    </FullScreenDialog>
  );
}
